/**
 * Ad packs in the Video studio library. A master video that has a pack shows as one stacked card (PackCard) instead of
 * ten loose videos; opening it shows the pack (PackView): the master, then every format made from it, grouped by
 * shape — vertical 9:16, feed 4:5, landscape 16:9, the TV spot, the outdoor cut — each with its real file playing on
 * hover, the channels it was made for (their logos), and whether it passes their specs. Clicking any of them, the
 * master included, opens it in the editor.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Check, Clapperboard, Download, Layers, Loader2, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { coworkApi, type VideoPackItem, type VideoPackPlatform, type VideoPackRole, type VideoSummary } from '@/cowork/omniad/api/coworkApi';
import type { VideoAspect } from '@/video/spec';
import { ChannelLogo, ChannelStack } from './ChannelLogo';
import { CHANNEL_NAME, packChannels, packItemState, saveVideoFile } from './videoStudioShared';

/** The shelves of a pack, in the order a media buyer reads them; each lists the roles that belong on it. */
const SHELVES: Array<{ title: string; blurb: string; roles: VideoPackRole[] }> = [
  { title: 'Vertical · 9:16', blurb: 'Full-screen feeds — the hooks and cut-downs the optimisers test', roles: ['master', 'hook-b', 'hook-c', 'cut-15', 'cut-10', 'cut-6'] },
  { title: 'Feed · 4:5', blurb: 'In-feed placements', roles: ['feed'] },
  { title: 'Landscape · 16:9', blurb: 'In-stream and professional feeds', roles: ['landscape'] },
  { title: 'TV spot', blurb: 'Connected TV — sound on, broadcast loudness', roles: ['ctv'] },
  { title: 'Outdoor screen', blurb: 'Digital out-of-home — silent, read in passing', roles: ['dooh'] },
];


/** Card titles where the server's label is long (the shelf already says the shape and the logos say the channels). */
const TITLE: Partial<Record<VideoPackRole, string>> = { feed: 'Feed version', landscape: 'Landscape version', master: 'Master' };

/** The frame of each shape at one height, so a shelf lines up. */
const FRAME_H = 236;
const RATIO: Record<VideoAspect, number> = { '9:16': 9 / 16, '4:5': 4 / 5, '1:1': 1, '16:9': 16 / 9 };

/** A master with a pack, as one card: the poster on a stack of two more, the format count and the channels' logos. */
export function PackCard({ video, onOpen }: { video: VideoSummary; onOpen: () => void }) {
  const items = video.pack?.items || [];
  const making = video.pack?.status === 'making';
  return (
    <button type="button" onClick={onOpen} className="group relative flex flex-col pt-3 text-left">
      {/* The stack: two cards behind, fanning out on hover. */}
      <span aria-hidden className="absolute inset-x-2 top-1 h-[calc(100%-60px)] origin-bottom rotate-[-5deg] rounded-xl bg-[#be43ff]/35 shadow-sm transition-transform duration-300 group-hover:rotate-[-8deg]" />
      <span aria-hidden className="absolute inset-x-2 top-1 h-[calc(100%-60px)] origin-bottom rotate-[4deg] rounded-xl bg-[#7a1fbf]/55 shadow-sm transition-transform duration-300 group-hover:rotate-[7deg]" />
      <span className="relative flex flex-col overflow-hidden rounded-xl border border-[#eaecf0] bg-white shadow-[0_4px_14px_rgba(46,2,76,0.12)] transition-shadow group-hover:shadow-[0_10px_28px_rgba(46,2,76,0.22)]">
        <span className="relative aspect-[4/5] w-full bg-[#2e024c]">
          {video.poster ? <img src={video.poster} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" /> : <Clapperboard className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 text-[#a9f600]" />}
          <span className="absolute inset-0 bg-gradient-to-t from-[#2e024c]/85 via-transparent to-transparent" />
          <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-md bg-[#a9f600] px-1.5 py-0.5 text-[11px] font-bold text-[#2e024c]"><Layers className="h-3 w-3" />Ad pack</span>
          <span className="absolute inset-x-2.5 bottom-2.5 flex items-end justify-between gap-2">
            <span className="flex flex-col text-white">
              <span className="text-[20px] font-bold leading-none">{items.length + 1}</span>
              <span className="text-[11px] font-semibold opacity-85">{making ? 'formats · making…' : 'formats'}</span>
            </span>
            {making && <Loader2 className="h-4 w-4 animate-spin text-white" />}
          </span>
        </span>
        <span className="flex flex-col gap-1.5 p-2.5">
          <span className="line-clamp-2 text-[13px] font-semibold text-[#101828]">{video.title}</span>
          <ChannelStack platforms={packChannels(items)} size={20} max={7} />
        </span>
      </span>
    </button>
  );
}

/** One format: its real file in its real shape (playing on hover), what it is, and the channels it is for. */
function FormatCard({ title, aspect, seconds, platforms, item, poster, master, onOpen }: {
  title: string; aspect: VideoAspect; seconds?: number; platforms: VideoPackPlatform[]; item?: VideoPackItem; poster: string | null; master?: boolean; onOpen: () => void;
}) {
  const [saving, setSaving] = useState(false);
  // The still shows at rest; the video itself mounts only while hovered (browsers decode few videos at once).
  const [live, setLive] = useState(false);
  const url = item ? item.render?.url : null;
  const still = item?.render?.posterUrl || null;
  const st = item ? packItemState(item) : null;
  const checks = item?.checks?.filter((c) => item.targets.includes(c.platform)) || [];
  const fails = checks.filter((c) => !c.ok);
  const width = Math.round(FRAME_H * RATIO[aspect]);
  async function save(e: React.MouseEvent) {
    e.stopPropagation();
    if (!item) return;
    setSaving(true);
    try { toast.success(`Saved ${await saveVideoFile(item.id)}`); } catch (err) { toast.error((err as Error).message); } finally { setSaving(false); }
  }
  return (
    <div className="flex shrink-0 snap-start flex-col gap-2" style={{ width: Math.max(width, 150) }}>
      <button type="button" onClick={onOpen} aria-label={`Open ${title} in the editor`}
        onMouseEnter={() => setLive(true)} onMouseLeave={() => setLive(false)} onFocus={() => setLive(true)} onBlur={() => setLive(false)}
        style={{ width, height: FRAME_H }}
        className={`group relative overflow-hidden rounded-[16px] bg-[#101828] shadow-[0_6px_18px_rgba(16,24,40,0.14)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_14px_32px_rgba(16,24,40,0.24)] ${master ? 'ring-[3px] ring-[#a9f600]' : ''}`}>
        {url && live ? <video src={url} poster={still || undefined} autoPlay muted playsInline loop className="h-full w-full object-cover" />
          : still ? <img src={still} alt="" className="h-full w-full object-cover" />
          : url ? <video src={`${url}#t=0.4`} muted playsInline preload="metadata" className="h-full w-full object-cover" />
          : poster ? <img src={poster} alt="" className="h-full w-full object-cover opacity-60" referrerPolicy="no-referrer" /> : null}
        <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/25" />
        <span className="absolute left-2 top-2 rounded-md bg-black/55 px-1.5 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm">{aspect}{seconds ? ` · ${seconds.toFixed(0)} s` : ''}</span>
        {master && <span className="absolute right-2 top-2 rounded-md bg-[#a9f600] px-1.5 py-0.5 text-[11px] font-bold text-[#2e024c]">Master</span>}
        {st?.busy && <span className="absolute inset-x-2 bottom-2 flex items-center gap-1.5 rounded-md bg-black/60 px-2 py-1 text-[11px] text-white"><Loader2 className="h-3 w-3 shrink-0 animate-spin" /><span className="truncate">{st.text}</span></span>}
        {st?.failed && <span className="absolute inset-x-2 bottom-2 rounded-md bg-[#b42318] px-2 py-1 text-[11px] font-semibold text-white">{st.text}</span>}
        {url && <span className="absolute bottom-2 right-2 rounded-full bg-white/90 px-2 py-0.5 text-[10.5px] font-bold text-[#101828] opacity-0 transition-opacity group-hover:opacity-100">Edit</span>}
      </button>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-start justify-between gap-2">
          <span className="text-[13px] font-semibold leading-snug text-[#101828]">{title}</span>
          {url && <button type="button" aria-label={`Download ${title}`} disabled={saving} onClick={(e) => void save(e)} className="shrink-0 rounded-md p-1 text-[#667085] hover:bg-[#f2f4f7] disabled:opacity-50">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}</button>}
        </div>
        {/* The channels it is for: their logos in a row (amber ring = fails that channel's specs), names underneath. */}
        {platforms.length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap gap-1">
              {platforms.map((p) => {
                const c = checks.find((x) => x.platform === p);
                return <span key={p} title={c && !c.ok ? `${CHANNEL_NAME[p]}: ${c.fails.join(' ')}` : CHANNEL_NAME[p]} className={`rounded-full ${c && !c.ok ? 'ring-2 ring-[#f79009]' : ''}`}><ChannelLogo platform={p} size={22} /></span>;
              })}
            </div>
            <span className="text-[11.5px] leading-snug text-[#667085]">{platforms.map((p) => CHANNEL_NAME[p]).join(' · ')}</span>
          </div>
        )}
        {item?.render?.status === 'done' && (fails.length
          ? <span className="flex items-start gap-1 text-[11.5px] text-[#93370d]"><TriangleAlert className="mt-px h-3 w-3 shrink-0" />{fails[0].fails[0]}</span>
          : <span className="flex items-center gap-1 text-[11.5px] font-semibold text-[#3b7d0b]"><Check className="h-3 w-3" />Passes their specs</span>)}
      </div>
    </div>
  );
}

/** The pack of one master: its formats on shelves by shape. Polls while variants are still being made. */
export function PackView({ id, onBack, onOpen }: { id: string; onBack: () => void; onOpen: (id: string) => void }) {
  const master = useQuery({ queryKey: ['videos', 'get', id], queryFn: () => coworkApi.videos.get(id) });
  const pack = useQuery({
    queryKey: ['videos', 'pack', id],
    queryFn: () => coworkApi.videos.packStatus(id),
    refetchInterval: (q) => { const d = q.state.data; return d?.pack?.status === 'making' || d?.items.some((it) => packItemState(it).busy) ? 3000 : false; },
  });
  const v = master.data?.video;
  const items = pack.data?.items || [];
  const coverage = pack.data?.coverage || [];
  const poster = v?.spec.scenes.map((s) => ('media' in s ? s.media : null)).find((m) => m?.posterUrl)?.posterUrl || v?.media.find((m) => m.posterUrl)?.posterUrl || null;
  const seconds = v ? Math.round(v.spec.scenes.reduce((n, s) => n + s.duration, 0)) : undefined;
  if (master.isLoading || pack.isLoading) return <div className="grid h-60 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-[#98a2b3]" /></div>;
  if (!v) return <div className="p-8 text-[14px] text-[#475467]">This video is gone. <button type="button" onClick={onBack} className="font-semibold underline">Back to the studio</button></div>;
  const covered = coverage.filter((c) => c.covered);
  return (
    <div className="flex flex-col gap-7">
      <header className="flex flex-col gap-3">
        <button type="button" onClick={onBack} className="inline-flex w-fit items-center gap-1.5 text-[13px] font-semibold text-[#475467] hover:text-[#101828]"><ArrowLeft className="h-4 w-4" />Video studio</button>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1">
            <span className="inline-flex w-fit items-center gap-1 rounded-md bg-[#a9f600] px-1.5 py-0.5 text-[11px] font-bold text-[#2e024c]"><Layers className="h-3 w-3" />Ad pack</span>
            <h1 className="text-[24px] font-semibold tracking-[-0.01em] text-[#101828]">{v.title}</h1>
            <p className="text-[13.5px] text-[#475467]">The master and {items.length} formats made from it · {covered.length} of {coverage.length} channels covered{pack.data?.pack?.status === 'making' ? ' · still making' : ''}.</p>
          </div>
          {/* Every channel the pack serves, as the client will see it. */}
          <div className="flex flex-wrap gap-1.5">
            {coverage.map((c) => (
              <span key={c.platform} className={`inline-flex items-center gap-1.5 rounded-full border py-1 pl-1 pr-2.5 text-[12px] font-semibold ${c.covered ? 'border-[#eaecf0] bg-white text-[#344054]' : 'border-dashed border-[#d0d5dd] text-[#98a2b3]'}`}>
                <span className={c.covered ? '' : 'opacity-40 grayscale'}><ChannelLogo platform={c.platform} size={20} /></span>{CHANNEL_NAME[c.platform]}
              </span>
            ))}
          </div>
        </div>
      </header>
      {pack.data?.pack?.notes?.length ? <p className="-mt-3 text-[12.5px] text-[#667085]">{pack.data.pack.notes.join(' ')}</p> : null}
      {SHELVES.map((shelf) => {
        const onShelf = items.filter((it) => shelf.roles.includes(it.role));
        const hasMaster = shelf.roles.includes('master') && v.spec.aspect === '9:16';
        if (!onShelf.length && !hasMaster) return null;
        const channels = packChannels(onShelf);
        return (
          <section key={shelf.title} className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h2 className="text-[16px] font-semibold text-[#101828]">{shelf.title}</h2>
              <span className="text-[12.5px] text-[#667085]">{shelf.blurb}</span>
              {channels.length > 0 && <ChannelStack platforms={channels} size={20} />}
            </div>
            <div className="-mx-2 flex snap-x gap-5 overflow-x-auto px-2 pb-3 pt-1 [scrollbar-width:thin]">
              {hasMaster && <FormatCard master title="Master" aspect={v.spec.aspect} seconds={seconds} platforms={[]} poster={poster} onOpen={() => onOpen(v.id)} />}
              {onShelf.map((it) => <FormatCard key={it.id} title={TITLE[it.role] || it.label} aspect={it.aspect || '9:16'} seconds={it.seconds} platforms={it.targets} item={it} poster={poster} onOpen={() => onOpen(it.id)} />)}
            </div>
          </section>
        );
      })}
      {/* A master in another shape (4:5, 16:9) still opens from here. */}
      {v.spec.aspect !== '9:16' && (
        <section className="flex flex-col gap-3">
          <h2 className="text-[16px] font-semibold text-[#101828]">Master</h2>
          <div className="flex"><FormatCard master title="Master" aspect={v.spec.aspect} seconds={seconds} platforms={[]} poster={poster} onOpen={() => onOpen(v.id)} /></div>
        </section>
      )}
    </div>
  );
}
